import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { ClipboardEdit } from 'lucide-react'
import { useCaseReview } from '@/hooks/useCaseReview'
import { useDocumentation } from '@/hooks/useDocumentation'
import { useProviderAuth } from '@/hooks/auth'
import { useRecordingNotes } from '@/hooks/useRecordings'
import PatientHeader from '@/components/case-review/PatientHeader'
import PreVisitContent from '@/components/case-review/PreVisitContent'
import DocumentationTab from '@/components/documentation/DocumentationTab'
import RecordingAttachment from '@/components/documentation/RecordingAttachment'
import VisitSummaryCard from '@/components/documentation/VisitSummaryCard'
import ClinicalNoteEditor from '@/components/documentation/ClinicalNoteEditor'
import IntakePanel from '@/components/case-review/IntakePanel'
import PhotoGallery from '@/components/documentation/PhotoGallery'
import MedicalHistoryPanel from '@/components/case-review/MedicalHistoryPanel'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'

type DocTab = 'treatment-plan' | 'visit-details' | 'photos' | 'medical-history'

const TAB_CONFIG: { key: DocTab; label: string }[] = [
  { key: 'visit-details', label: 'Visit Details' },
  { key: 'treatment-plan', label: 'Treatment Plan' },
  { key: 'photos', label: 'Photos' },
  { key: 'medical-history', label: 'Medical History' },
]

function hasStartDocumentation(state: unknown): state is { startDocumentation: boolean } {
  return typeof state === 'object' && state !== null && 'startDocumentation' in state
}

export default function CaseReviewPage() {
  const { visitId } = useParams<{ visitId: string }>()
  const location = useLocation()
  const { data: bundle, isLoading, error, refetch } = useCaseReview(visitId ?? '')
  const { state: docState, dispatch, clearDraft } = useDocumentation(visitId)
  const { provider } = useProviderAuth()
  const autoStartFired = useRef(false)
  const [activeTab, setActiveTab] = useState<DocTab>('visit-details')

  const { data: notesData } = useRecordingNotes(docState.recording?.id)
  const notes = notesData?.notes ?? []
  const providerName = provider?.name ?? provider?.username ?? 'Provider'

  // Reset auto-start ref when visitId changes (component reused for different visit)
  useEffect(() => {
    autoStartFired.current = false
  }, [visitId])

  // Auto-start documentation when navigated from "Start Documentation" button
  // or when viewing a completed visit
  const shouldAutoStart = hasStartDocumentation(location.state) && location.state.startDocumentation
  useEffect(() => {
    if (
      (shouldAutoStart || bundle?.visit.status === 'completed') &&
      bundle &&
      docState.mode === 'pre-visit' &&
      !autoStartFired.current
    ) {
      autoStartFired.current = true
      dispatch({ type: 'START_DOCUMENTATION' })
    }
  }, [shouldAutoStart, bundle, docState.mode, dispatch])

  const handleTabKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentIndex = TAB_CONFIG.findIndex((t) => t.key === activeTab)
    let nextIndex: number | null = null
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      nextIndex = (currentIndex + 1) % TAB_CONFIG.length
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      nextIndex = (currentIndex - 1 + TAB_CONFIG.length) % TAB_CONFIG.length
    }
    if (nextIndex !== null) {
      setActiveTab(TAB_CONFIG[nextIndex].key)
      const nextButton = document.getElementById(`tab-${TAB_CONFIG[nextIndex].key}`)
      nextButton?.focus()
    }
  }, [activeTab])

  if (isLoading) {
    return <LoadingPage variant="detail" />
  }

  if (error || !bundle) {
    return (
      <ErrorState
        message={error?.message ?? 'Visit not found'}
        onRetry={() => refetch()}
      />
    )
  }

  // Pre-visit mode: show existing layout + Start Documentation button (completed visits only)
  if (docState.mode === 'pre-visit') {
    return (
      <div className="space-y-6">
        <PatientHeader patient={bundle.patient} medicalHistory={bundle.medical_history} />

        {bundle.visit.status === 'completed' && (
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <ClipboardEdit className="h-4 w-4" />
              <span>This visit needs documentation review</span>
            </div>
            <Button
              onClick={() => dispatch({ type: 'START_DOCUMENTATION' })}
            >
              <ClipboardEdit className="h-4 w-4 mr-2" />
              Start Documentation
            </Button>
          </div>
        )}

        <PreVisitContent bundle={bundle} />
      </div>
    )
  }

  // Documentation mode: 4-tab layout
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex-none pb-4">
        <PatientHeader patient={bundle.patient} medicalHistory={bundle.medical_history} />

        <div className="mt-4 flex flex-wrap items-center gap-1" role="tablist" onKeyDown={handleTabKeyDown}>
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              id={`tab-${tab.key}`}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`tabpanel-${tab.key}`}
              tabIndex={activeTab === tab.key ? 0 : -1}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'treatment-plan' && (
        <div role="tabpanel" id="tabpanel-treatment-plan" aria-labelledby="tab-treatment-plan">
          <ErrorBoundary fallback={<p className="py-8 text-center text-sm text-red-600">Documentation failed to load. Please refresh the page.</p>}>
            <DocumentationTab
              visitId={bundle.visit.id}
              state={docState}
              dispatch={dispatch}
              clearDraft={clearDraft}
            />
          </ErrorBoundary>
        </div>
      )}

      {activeTab === 'visit-details' && (
        <div role="tabpanel" id="tabpanel-visit-details" aria-labelledby="tab-visit-details" className="flex-1 overflow-y-auto space-y-6">
          <RecordingAttachment
            recording={docState.recording}
            dispatch={dispatch}
            visitId={bundle.visit.id}
            visitScheduledAt={bundle.visit.scheduled_at}
          />
          <VisitSummaryCard summary={docState.recording?.summary ?? null} />
          <IntakePanel intake={bundle.intake} title="Condition" />
          <ClinicalNoteEditor
            notes={notes}
            selectedNote={docState.selectedNote}
            noteContent={docState.noteContent}
            noteApproval={docState.noteApproval}
            addendums={docState.addendums}
            dispatch={dispatch}
            providerName={providerName}
          />
        </div>
      )}

      {activeTab === 'photos' && (
        <div role="tabpanel" id="tabpanel-photos" aria-labelledby="tab-photos" className="flex-1 overflow-y-auto space-y-6">
          <PhotoGallery
            photos={bundle.photos}
            referencePhotos={bundle.reference_photos}
            currentVisitDate={bundle.visit.scheduled_at}
          />
        </div>
      )}

      {activeTab === 'medical-history' && (
        <div role="tabpanel" id="tabpanel-medical-history" aria-labelledby="tab-medical-history" className="flex-1 overflow-y-auto">
          <MedicalHistoryPanel medicalHistory={bundle.medical_history} />
        </div>
      )}
    </div>
  )
}
